import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {MiniStatusIconProps} from './MiniStatusIcon'
import {MiniMergeStatusBoundary} from './MiniMergeStatusBoundary'

import {isSuccessStatus, type CombinedState} from '@github-ui/mergebox/page-data/payloads/status-checks'
import {useSyncFavicon} from '@github-ui/mergebox/use-sync-favicon'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {useMergeabilityData} from '../use-mergeability-data'

const iconTypeByCombinedState: Record<CombinedState, MiniStatusIconProps['type']> = {
  PASSED: 'success',
  PENDING: 'warning',
  PENDING_APPROVAL: 'warning',
  PENDING_CONFLICTS: 'warning',
  FAILED: 'failure',
  PENDING_FAILED: 'failure',
  SOME_FAILED: 'failure',
}

export function ChecksMiniStatusBoundary({children}: {children: React.ReactNode}) {
  const goToMergeBox = useGoToMergeBox()

  return (
    <MiniMergeStatusBoundary
      action={goToMergeBox}
      icon={ChevronDownIcon}
      actionLabel="View checks"
      boundaryName="ChecksMiniStatusBoundary"
      text="Checks"
    >
      {children}
    </MiniMergeStatusBoundary>
  )
}

export function ChecksMiniStatus({expanded, onToggle}: {expanded?: boolean; onToggle: () => void}) {
  const data = useMergeabilityData()
  useSyncFavicon(data?.statusRollup, {isInMergeQueue: data?.mergeBoxPullRequest.isInMergeQueue})

  const rollupSummary = data?.statusRollup.summary || []
  const status = data ? undefined : 'Checks unavailable'

  let successfulChecks = 0
  let totalChecks = 0

  for (const summary of rollupSummary) {
    totalChecks += summary.count

    if (isSuccessStatus(summary.state)) {
      successfulChecks += summary.count
    }
  }

  const pendingWorkflowCount = data?.statusRollup.pendingWorkflowApprovalRollup?.workflowsRequiringApprovalCount ?? 0
  const activeStateMinimum = data && data.statusRollup.combinedState !== 'PASSED' ? 1 : 0
  totalChecks = Math.max(totalChecks, pendingWorkflowCount, activeStateMinimum)

  const isUnavailable = Boolean(data) && totalChecks === 0
  const icon = data ? iconTypeByCombinedState[data.statusRollup.combinedState] : 'error'
  const actionLabel = expanded ? 'Collapse checks' : 'Expand checks'
  const arrowIcon = expanded ? ChevronDownIcon : ChevronRightIcon

  return (
    <MiniMergeStatusItem aria-label={actionLabel} aria-expanded={expanded} onClick={onToggle}>
      <MiniMergeStatusItem.LabelRow icon={arrowIcon}>
        <MiniMergeStatusItem.Label
          icon={icon}
          isUnavailable={isUnavailable}
          status={status}
          successful={successfulChecks}
          total={totalChecks}
        >
          Checks
        </MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={`${successfulChecks} of ${totalChecks} successful checks`}
        isUnavailable={isUnavailable}
        successful={successfulChecks}
        total={totalChecks}
        unavailableLabel="No checks available"
      />
    </MiniMergeStatusItem>
  )
}
